/**
 * The guard rules.
 *
 * Pure functions, shared verbatim by two callers that must never disagree:
 *
 *  - the COMPILER, which is authoritative and refuses an invalid spec with a
 *    400 rather than running it;
 *  - the BUILDER UI, which calls the same functions to grey out a choice
 *    *before* it is made, so the refusal is never a surprise on submit.
 *
 * Duplicating this logic in the UI is what produces a builder that offers
 * combinations the server rejects, so it lives here and only here.
 *
 * What is NOT here: a rule refusing a ratio at a grain its denominator cannot
 * be split by. That case reports itself — the compiler divides through
 * `NULLIF`, so an undefined ratio arrives as NULL and renders as an em dash at
 * the cell it belongs to. Pre-empting it would need every dimension to declare
 * which fact rows carry it, an unverifiable claim about the view's shape, to
 * buy a better message rather than a better number.
 *
 * See docs/reporting/requirements.md §FR-1, §FR-2.
 */
import { type FilterNode, type FilterRule, isFilterGroup, opArity, opLabel } from './filter';
import {
  acceptedOperatorsFor,
  allowsKind,
  canGroup,
  canList,
  canSort,
  type Dimension,
  fieldLabel,
  filterValueType,
  findDimension,
  findMeasure,
  isAdditive,
  type Measure,
  type ReportModel,
} from './model';
import {
  allGroupBy,
  isLevelSort,
  MAX_GROUP_BY,
  REPORT_KIND_LABELS,
  type ReportSpec,
  type ShowAsMode,
  showAsFor,
  type UIReportSpec,
  VIZ_KINDS,
  type VizKind,
} from './spec';

/** A guard verdict. `reason` is user-facing prose, not a code. */
export type Verdict = { ok: true } | { ok: false; reason: string };

const ok: Verdict = { ok: true };
const no = (reason: string): Verdict => ({ ok: false, reason });

/** The axes a rule needs to see. */
type Axes = Pick<UIReportSpec, 'measures' | 'rows' | 'cols'>;

/** The axes plus how they are drawn — what a stacking rule needs. */
type Drawn = Axes & Pick<UIReportSpec, 'viz' | 'stacked'>;

/** Which dimensions are selectable right now, and why the rest are not. */
export function dimensionAvailability(
  model: ReportModel,
  spec: Pick<UIReportSpec, 'rows' | 'cols'>,
): Map<string, Verdict> {
  const out = new Map<string, Verdict>();
  const chosen = new Set(allGroupBy(spec));
  const atCeiling = chosen.size >= MAX_GROUP_BY;

  for (const dim of model.dimensions) {
    if (chosen.has(dim.id)) {
      out.set(dim.id, ok);
      continue;
    }
    if (!canGroup(dim)) {
      out.set(dim.id, no(`${dim.label} cannot be grouped by.`));
      continue;
    }
    out.set(
      dim.id,
      atCeiling ? no(`${MAX_GROUP_BY} group-bys is the ceiling — remove one first.`) : ok,
    );
  }
  return out;
}

/**
 * What a detail listing may put in a column, and why the rest may not.
 *
 * `capability.groupable` is NOT consulted, and that is the point of this
 * function existing beside {@link dimensionAvailability}. Keka ID, QB ID, a
 * free-text description and a comma-joined approver list are bad pivot axes and
 * perfectly good table columns: grouping by a correlation id gives one row per
 * employee with worse labels, while listing one is how you reconcile an export.
 * A listing has no axes to be bad on, so every dimension is available and the
 * only refusals are about MEASURES, which have to declare a row-level form.
 *
 * Keyed by field id, over dimensions and measures both — a listing's column may
 * be either, and the picker offers them from one list.
 */
export function columnAvailability(model: ReportModel): Map<string, Verdict> {
  const out = new Map<string, Verdict>();
  for (const dim of model.dimensions) out.set(dim.id, ok);
  for (const measure of model.measures) {
    out.set(
      measure.id,
      canList(model, measure)
        ? ok
        : no(`${measure.label} is a total, and has no value for a single row.`),
    );
  }
  return out;
}

/** Whether a refusal lifts as soon as something is removed. */
export function isDepthRefusal(v: Verdict): boolean {
  return !v.ok && v.reason.includes('ceiling');
}

/** Dimensions an axis may be sorted by. */
export function sortableDimensions(model: ReportModel): Dimension[] {
  return model.dimensions.filter(canSort);
}

/**
 * "Show as" availability.
 *
 * A share is a part of a SUM, so it is only meaningful for an additive measure
 * — a percentage of an average or of a ratio is not a number anyone can act on.
 *
 * The structural exception is a share taken across an axis with no dimension on
 * it: with an empty column axis every row holds a single cell, so "% of row"
 * divides that cell by itself and prints 100% the whole way down.
 */
export function canShowAs(measure: Measure, mode: ShowAsMode, axes?: Axes): Verdict {
  if (mode === 'raw') return ok;
  if (measure.agg === 'ratio') {
    return no(
      `${measure.label} is already a ratio — a percentage of a percentage is not meaningful.`,
    );
  }
  if (!isAdditive(measure)) {
    return no(`${measure.label} is not additive, so it has no share of a total.`);
  }
  if (mode === 'pctRow' && axes && axes.cols.length === 0) {
    return no(
      'A share of the row needs a column dimension to share between — with none, every cell is the whole row and reads 100%.',
    );
  }
  // The mirror image, and it only reaches here from the builder: a draft may
  // have an empty row shelf, while a runnable spec never can. Which is exactly
  // why the builder is the only place that can refuse it.
  if (mode === 'pctCol' && axes && axes.rows.length === 0) {
    return no(
      'A share of the column needs a row dimension to share between — with none, every cell is the whole column and reads 100%.',
    );
  }
  return ok;
}

/** Visualization gating by data shape (FR-2.11). */
export function canVisualize(model: ReportModel, spec: Axes, viz: VizKind): Verdict {
  const nr = spec.rows.length;
  const nc = spec.cols.length;
  const measures = spec.measures.map((id) => findMeasure(model, id)).filter(isDefined);

  if (viz === 'table') return ok;

  if (viz === 'pie') {
    if (measures.some((m) => !isAdditive(m)))
      return no('A pie shows parts of a whole — a non-additive measure has no whole to divide.');
    if (nc > 0) {
      const first = findDimension(model, spec.cols[0] ?? '');
      return no(
        `A pie has no series axis. Move ${first?.label ?? 'the column dimension'} to Rows, or remove it.`,
      );
    }
    if (nr !== 1) return no(`A pie shows one row dimension. You have ${nr}.`);
    if (measures.length > 1) return no('A pie shows one measure.');
    return ok;
  }

  // A KPI is one number, so it is the only viz that wants NO axes — and the
  // refusals say "remove", where every other viz's say "move to Columns".
  // There is nowhere to move a dimension TO on a chart that has no axis.
  // A metric, optionally WITH ITS HISTORY.
  //
  // Two shapes, not one. With no rows it is the number over the whole period.
  // With one TIME dimension it is the same number per period: the last is
  // what the tile shows, the one before it is what the movement is measured
  // against, and the whole series is the sparkline. That is one query
  // answering all three, rather than three questions that could disagree.
  //
  // A categorical row is refused. "Hours by customer" has no last value —
  // whichever it ended up being is an artefact of the sort — so a delta
  // computed off it would be a number about the ordering, not about the
  // business.
  if (viz === 'metric') {
    if (measures.length !== 1) {
      return no(`A metric shows one measure. You have ${measures.length}.`);
    }
    if (nr > 1) {
      return no(`A metric trends over one time dimension. You have ${nr} on Rows.`);
    }
    if (nr === 1) {
      const first = findDimension(model, spec.rows[0] ?? '');
      if (!first?.isTime) {
        return no(
          `A metric trends over TIME. Remove ${first?.label ?? 'that dimension'} from Rows, or group by a period instead.`,
        );
      }
    }
    if (nc > 0) {
      const first = findDimension(model, spec.cols[0] ?? '');
      return no(
        `A metric has no series axis. Remove ${first?.label ?? 'the column dimension'} from Columns.`,
      );
    }
    return ok;
  }

  if (viz === 'bar') {
    if (nr > 2) return no(`A bar nests two row dimensions. With ${nr}, use a table.`);
    if (nc > 1)
      return no(`A bar carries one series dimension. With ${nc} on Columns, use a table.`);
    return ok;
  }

  // Line and area, together: an area IS a filled line, so it carries the same
  // requirement for the same reason. A fill reads as accumulation between two
  // ordered points, and a categorical axis has no order for it to accumulate
  // along — the shape would be an artefact of however the rows happened to
  // sort. Split into two branches only to name the one the reader picked.
  const word = viz === 'area' ? 'An area' : 'A line';
  const hasTime = spec.rows.some((id) => findDimension(model, id)?.isTime);
  if (!hasTime) return no(`${word} needs a time dimension on the row axis.`);
  if (nr > 1)
    return no(`${word} carries time plus series — move the extra row dimension to Columns.`);
  if (nc > 1) return no(`${word} carries one series dimension.`);
  return ok;
}

/**
 * Whether this drawing may stack.
 *
 * SEPARATE from {@link canVisualize} because it answers a different question:
 * `viz` decides what shape is drawn and `stacked` decides what the shape
 * means, so a spec can be a perfectly legal bar and an illegal stacked bar.
 * Folding it in would make the bar option itself unavailable and send the
 * reader to fix the wrong control.
 *
 * Returns `ok` when nothing is stacked, so callers need no `if` around it.
 */
export function canStack(model: ReportModel, spec: Drawn): Verdict {
  if (!spec.stacked) return ok;
  if (spec.viz !== 'bar' && spec.viz !== 'area') {
    return no(
      `Only a bar or an area stacks. This is ${spec.viz === 'metric' ? 'a metric' : `a ${spec.viz}`}.`,
    );
  }
  // Stacking is what turns a series into a part of a total, so without a
  // series axis there is nothing to stack — one band per category is the
  // unstacked chart with a misleading label on the control.
  if (spec.cols.length !== 1) {
    return no('Stacking needs one series dimension on Columns — that is what the bands are.');
  }
  // The stack HEIGHT is a sum, and summing a ratio produces a number that is
  // not a quantity of anything. Same rule a pie makes, and for the same reason.
  const nonAdditive = spec.measures
    .map((id) => findMeasure(model, id))
    .filter(isDefined)
    .filter((m) => !isAdditive(m));
  if (nonAdditive.length > 0) {
    return no(
      `A stack adds its bands up, and ${nonAdditive[0]?.label} does not add up. Turn stacking off.`,
    );
  }
  return ok;
}

/**
 * The viz options, each with its verdict, in display order.
 *
 * Iterates {@link VIZ_KINDS} rather than a list of its own. A second copy is
 * free to fall behind the vocabulary, and the failure is silent in the worst
 * direction: the new option is simply missing from the picker, which reads as
 * a chart type the report cannot do rather than as one nobody listed.
 */
export function vizAvailability(model: ReportModel, spec: Axes): Map<VizKind, Verdict> {
  const out = new Map<VizKind, Verdict>();
  for (const viz of VIZ_KINDS) {
    out.set(viz, canVisualize(model, spec, viz));
  }
  return out;
}

/**
 * Full validation of a spec against a model. The compiler calls this before
 * emitting any SQL; the first failure wins, so the message is specific.
 *
 * Both kinds share the model, the period and the filters — they mean the same
 * thing in a listing as in a pivot, which is also why switching kind keeps
 * them. Everything after that is per kind.
 */
/**
 * The filter nodes: every rule answerable, every operator one its column has.
 *
 * Shared by the builder and the compiler, like every other rule in this file,
 * because a filter the UI lets you build and the server refuses is worse than
 * either behaviour on its own. What is NOT here is whether an `or` group's
 * leaves land on one relation — that question needs the model's SQL, which
 * this package deliberately cannot see, so the compiler asks it.
 */
export function validateFilters(model: ReportModel, nodes: readonly FilterNode[]): Verdict {
  const seen = new Set<string>();
  for (const node of nodes) {
    if (seen.has(node.id)) return no(`Two filters share the id "${node.id}".`);
    seen.add(node.id);
    for (const rule of isFilterGroup(node) ? node.children : [node]) {
      const verdict = validateRule(model, rule);
      if (!verdict.ok) return verdict;
    }
  }
  return ok;
}

function validateRule(model: ReportModel, rule: FilterRule): Verdict {
  const dim = findDimension(model, rule.field);
  const measure = dim ? undefined : findMeasure(model, rule.field);
  if (!dim && !measure) return no(`Unknown filter "${rule.field}".`);

  const label = fieldLabel(model, rule.field);
  const type = filterValueType(model, rule.field);
  if (!type) {
    // Two different refusals, because they send the reader somewhere
    // different: a dimension the model closed, and a measure with no per-row
    // value to compare. The second is not a setting anybody can change.
    return no(
      measure
        ? `${label} is an aggregate with no per-row value, so it cannot be filtered. Filter the columns it is built from instead.`
        : `${label} cannot be filtered on.`,
    );
  }

  // The FIELD's operators, not the type's: a measure's list is narrower than a
  // number column's, because `is any of` exists on the numeric types only to
  // keep filters saved before operators valid, and no measure has any.
  // What a spec may CARRY, which is wider than what a chip OFFERS: the blank
  // checks hold on any dimension (see `acceptedOperatorsFor`).
  if (!acceptedOperatorsFor(model, rule.field).includes(rule.op)) {
    return no(`${label} cannot be filtered with "${opLabel(rule.op, type)}".`);
  }

  // The arity is the operator's contract with its value, and the two parting
  // company is how `between` reaches the compiler holding one number.
  const arity = opArity(rule.op, type);
  if (arity === 'none') {
    return rule.value ? no(`"${opLabel(rule.op, type)}" takes no value.`) : ok;
  }
  if (!rule.value) return no(`${label} ${opLabel(rule.op, type)} needs a value.`);
  if (rule.value.kind !== arity) {
    return no(`${label} ${opLabel(rule.op, type)} needs a ${arity} value.`);
  }
  // `custom` is a preset in name only: it resolves to no window without dates,
  // so a rule carrying it compiles to nothing and is then refused for reading
  // a column no relation exposes — a true sentence about the wrong half.
  if (rule.value.kind === 'macro' && rule.value.preset === 'custom') {
    return no(`${label} is within needs a named window — use "is between" for two dates.`);
  }
  // A range open at both ends narrows nothing while the chip says it does.
  if (rule.value.kind === 'numberRange' && rule.value.min === null && rule.value.max === null) {
    return no(`${label} is between needs at least one end.`);
  }
  if (rule.value.kind === 'dateRange' && rule.value.from === null && rule.value.to === null) {
    return no(`${label} is between needs at least one end.`);
  }
  return ok;
}

export function validateSpec(model: ReportModel, spec: ReportSpec): Verdict {
  if (spec.model !== model.id) return no(`Spec targets model "${spec.model}", not "${model.id}".`);
  if (spec.to < spec.from) return no('The end date is before the start date.');
  // Before the per-kind rules, because a refused kind makes the rest of them
  // the wrong questions: a utilization listing would otherwise be turned down
  // for the columns it picked rather than for being a listing at all.
  if (!allowsKind(model, spec.kind)) {
    return no(`${model.label} cannot be built as a ${REPORT_KIND_LABELS[spec.kind]} report.`);
  }

  const filters = validateFilters(model, spec.filters);
  if (!filters.ok) return filters;

  return spec.kind === 'detail' ? validateDetail(model, spec) : validatePivot(model, spec);
}

/**
 * A listing: columns, an order, a page.
 *
 * No ceiling on how many columns — the Timesheet report has twenty-two, and a
 * ceiling that stopped it would be a ceiling on the report this exists to
 * express. No `groupable` check, for the reason {@link columnAvailability}
 * gives. The pivot-only settings are IGNORED rather than refused: switching
 * kind is meant to be reversible, and a draft that kept its axes while you look
 * at the listing is friendlier than one that refuses to run until you clear
 * them.
 */
function validateDetail(model: ReportModel, spec: ReportSpec): Verdict {
  if (spec.columns.length === 0) return no('Pick at least one column.');
  if (new Set(spec.columns).size !== spec.columns.length) {
    return no('The same column is listed twice.');
  }

  const available = columnAvailability(model);
  for (const id of spec.columns) {
    const verdict = available.get(id);
    if (!verdict) return no(`Unknown column "${id}".`);
    if (!verdict.ok) return verdict;
  }

  // Same rule as the pivot's, for the same reason: an `ORDER BY` naming
  // something the select list does not have is not a legal query. `label` is a
  // pivot idea — a listing has no group to be the label OF — so a listing sorts
  // by one of its own columns.
  for (const sort of spec.rowSort) {
    if (!spec.columns.includes(sort.by)) {
      return no(`The sort names "${sort.by}", which is not a column of this listing.`);
    }
    // `on` names a nesting LEVEL, and a listing has none — its rows are the
    // rows, ordered by one chain over the whole result. An entry carrying it
    // came from a pivot draft and would order by a level that is not there.
    if (sort.on !== undefined) {
      return no('A listing has no levels to order separately; it sorts by its columns.');
    }
    const dim = findDimension(model, sort.by);
    if (dim && !canSort(dim)) return no(`${dim.label} cannot be sorted by.`);
  }

  if (spec.viz !== 'table') return no('A listing is a table. Summarise it to chart it.');
  return ok;
}

/** A pivot: axes, aggregates, totals, and the group-by ceiling. */
function validatePivot(model: ReportModel, spec: ReportSpec): Verdict {
  const groupBy = allGroupBy(spec);
  if (groupBy.length > MAX_GROUP_BY) {
    return no(`${groupBy.length} group-bys requested; the ceiling is ${MAX_GROUP_BY}.`);
  }
  if (new Set(groupBy).size !== groupBy.length) {
    return no('The same dimension appears on both axes.');
  }

  for (const id of groupBy) {
    const dim = findDimension(model, id);
    if (!dim) return no(`Unknown dimension "${id}".`);
    if (!canGroup(dim)) return no(`${dim.label} cannot be grouped by.`);
  }

  for (const id of spec.measures) {
    const measure = findMeasure(model, id);
    if (!measure) return no(`Unknown measure "${id}".`);

    const showAs = canShowAs(measure, showAsFor(spec, id), spec);
    if (!showAs.ok) return showAs;
  }

  // A sort must name something the result will actually contain. The builder
  // prunes a sort whose measure is deselected, but the builder is not the gate
  // — a saved report or a hand-written request arrives here directly, and
  // ordering by a measure the query does not select is not a legal `ORDER BY`.
  for (const [axis, chain, dims] of [
    ['row', spec.rowSort, spec.rows],
    ['column', spec.colSort, spec.cols],
  ] as const) {
    for (const sort of chain) {
      // `on` names the LEVEL this entry orders. One that is not on this axis
      // orders nothing at all — the entry is inert, and the builder goes on
      // drawing it as the field's chosen order. Every other dangling id in a
      // spec earns a refusal; this one has to as well.
      if (sort.on !== undefined && !dims.includes(sort.on)) {
        return no(
          `The ${axis} sort orders "${sort.on}", which is not a ${axis} dimension of this report.`,
        );
      }
      if (isLevelSort(sort.by)) continue;
      if (!spec.measures.includes(sort.by)) {
        return no(`The ${axis} sort names "${sort.by}", which this report does not select.`);
      }
    }
  }

  // The display flags name ROW dimensions. An id that no longer resolves is
  // not harmless: the band simply never prints, with no message anywhere, and
  // a feature that silently does nothing is the one failure a reader cannot
  // diagnose. Every other id in a spec earns an "Unknown …" refusal.
  //
  // `toggledGroups` is NOT checked: it names data (a customer's key), not
  // vocabulary, and a group missing from this result is simply not drawn.
  for (const [what, ids] of [
    ['subtotal', spec.subtotals],
    ['hide-repeats', spec.suppressRepeats],
    ['collapse', spec.collapsed ?? []],
  ] as const) {
    for (const id of ids) {
      if (!spec.rows.includes(id)) {
        return no(`The ${what} setting names "${id}", which is not a row dimension here.`);
      }
    }
  }

  const drawable = canVisualize(model, spec, spec.viz);
  if (!drawable.ok) return drawable;
  return canStack(model, spec);
}

function isDefined<T>(v: T | undefined): v is T {
  return v !== undefined;
}
